/*
  <product-media-gallery>: a scroll-snap slider with thumbnails, previous and
  next buttons, a position indicator, click-to-load external videos, 3D models
  with an AR button where the device supports it, and an optional zoom dialog.
  Markup comes from snippets/product-media-gallery.liquid.
*/
if (!customElements.get('product-media-gallery')) {
  class ProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.stopListening = Theme.on('variant:changed', (event) => this.onVariantChange(event.detail));
      if (this.ready) return;
      this.ready = true;

      this.track = this.querySelector('[data-media-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-media-id]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-media-thumb]'));
      this.counter = this.querySelector('[data-media-counter]');
      this.previousButton = this.querySelector('[data-media-previous]');
      this.nextButton = this.querySelector('[data-media-next]');
      this.lightbox = this.querySelector('[data-media-lightbox]');
      this.index = 0;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.track.addEventListener('scroll', Theme.debounce(() => this.onScrollEnd(), 90), { passive: true });
      if (this.lightbox) this.lightbox.addEventListener('close', () => this.onLightboxClose());

      this.setUpAugmentedReality();
    }

    disconnectedCallback() {
      if (this.stopListening) this.stopListening();
    }

    onVariantChange(detail) {
      if (!detail || detail.sectionId !== this.dataset.sectionId || !this.slides) return;
      const media = detail.variant && detail.variant.featured_media;
      if (!media) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(media.id));
      if (index >= 0) this.show(index);
    }

    onClick(event) {
      const target = event.target;
      const thumbnail = target.closest('[data-media-thumb]');
      if (thumbnail) return this.show(this.thumbnails.indexOf(thumbnail));
      if (target.closest('[data-media-previous]')) return this.show(this.index - 1);
      if (target.closest('[data-media-next]')) return this.show(this.index + 1);

      const play = target.closest('[data-media-play]');
      if (play) return this.loadDeferred(play.closest('[data-deferred-media]'));

      const zoom = target.closest('[data-media-zoom]');
      if (zoom) return this.openLightbox(zoom);

      if (this.lightbox && (target.closest('[data-lightbox-close]') || target === this.lightbox || target.matches('[data-lightbox-stage]'))) {
        this.lightbox.close();
      }
      return undefined;
    }

    /* Arrow keys move between thumbnails; Home and End jump to the ends. */
    onKeydown(event) {
      const thumbnail = event.target.closest('[data-media-thumb]');
      if (!thumbnail) return;
      const current = this.thumbnails.indexOf(thumbnail);
      const last = this.thumbnails.length - 1;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current === last ? 0 : current + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current === 0 ? last : current - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.thumbnails[next].focus();
      this.show(next);
    }

    show(index) {
      if (index < 0 || index >= this.slides.length) return;
      const slide = this.slides[index];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setActive(index);
    }

    onScrollEnd() {
      const position = this.track.scrollLeft;
      let nearest = 0;
      this.slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - position) < Math.abs(this.slides[nearest].offsetLeft - position)) nearest = index;
      });
      if (nearest !== this.index) this.setActive(nearest);
    }

    setActive(index) {
      const previous = this.slides[this.index];
      this.index = index;
      if (previous && previous !== this.slides[index]) this.stopMedia(previous);

      this.thumbnails.forEach((thumbnail, position) => {
        if (position === index) {
          thumbnail.setAttribute('aria-current', 'true');
          if (thumbnail.offsetParent) {
            const list = thumbnail.closest('ul');
            list.scrollTo({ left: thumbnail.offsetLeft - list.clientWidth / 2 + thumbnail.clientWidth / 2 });
          }
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });

      if (this.counter) {
        this.counter.textContent = (this.counter.dataset.template || '').replace('[index]', index + 1);
      }
      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }

    /* Stops playback in a slide the shopper has moved away from. */
    stopMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      const deferred = slide.querySelector('[data-deferred-media]');
      if (deferred) {
        const frame = deferred.querySelector('[data-media-frame]');
        const poster = deferred.querySelector('[data-media-play]');
        if (frame && !frame.hidden) {
          frame.replaceChildren();
          frame.hidden = true;
          poster.hidden = false;
        }
      }
    }

    /* External videos are only requested once the shopper asks to play them. */
    loadDeferred(container) {
      if (!container) return;
      const template = container.querySelector('template');
      const frame = container.querySelector('[data-media-frame]');
      const poster = container.querySelector('[data-media-play]');
      if (!template || !frame) return;
      frame.replaceChildren(template.content.cloneNode(true));
      frame.hidden = false;
      poster.hidden = true;
      const player = frame.querySelector('iframe, video');
      if (player) {
        player.setAttribute('tabindex', '0');
        player.focus();
      }
    }

    openLightbox(trigger) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      const stage = this.lightbox.querySelector('[data-lightbox-stage]');
      const image = document.createElement('img');
      image.className = 'media-lightbox__image';
      image.alt = trigger.dataset.zoomAlt || '';
      image.sizes = '100vw';
      image.srcset = trigger.dataset.zoomSrcset || '';
      image.src = trigger.dataset.zoomSrc;
      stage.replaceChildren(image);
      this.lightbox.showModal();
      Theme.lockScroll();
    }

    onLightboxClose() {
      Theme.unlockScroll();
      const stage = this.lightbox.querySelector('[data-lightbox-stage]');
      if (stage) stage.replaceChildren();
    }

    /* Registers 3D models with Shopify's AR feature, which reveals the button on capable devices. */
    setUpAugmentedReality() {
      const data = this.querySelector('[data-model-json]');
      if (!data || !window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      const register = () => {
        if (!window.ShopifyXR) return;
        try {
          window.ShopifyXR.addModels(JSON.parse(data.textContent));
          window.ShopifyXR.setupXRElements();
        } catch (error) {
          /* AR stays hidden; the 3D viewer still works */
        }
      };

      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (errors) => {
            if (errors) return;
            if (window.ShopifyXR) register();
            else document.addEventListener('shopify_xr_initialized', register, { once: true });
          },
        },
      ]);
    }
  }

  customElements.define('product-media-gallery', ProductMediaGallery);
}
